import { memo, useMemo } from 'react';

import dayjs from 'dayjs';
import {
  Banknote,
  CalendarIcon,
  CheckCircle,
  CircleDollarSign,
  ClockIcon,
  Copy,
  Info,
  Layers,
  LucideIcon,
  Pencil,
  Percent,
  ScissorsIcon,
  StickyNote,
  Trash2,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { DollarSignOffIcon } from '@/assets/svgs/dollar-sign-off-icon';
import { UserRoundArrowLeftIcon } from '@/assets/svgs/user-round-arrow-left-icon';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Command, CommandSeparator } from '@/components/ui/command';
import { Separator } from '@/components/ui/separator';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';
import { formatHoursToTimeString } from '@/helpers';
import { cn } from '@/lib/utils';
import {
  EntityAvatar,
  OpsiumUserShortResponse,
  StatusBadge,
  StatusBadgeSize,
  StatusBadgeType,
} from '@/modules/common';
import { RateSource } from '@/modules/common/types/rate-source';
import { RateMask } from '@/modules/common/utils/rate-mask';
import { formatCompactDateRange, formatDisplayDate } from '@/utils/date-formatters';

import { formatTotalBookedHours } from '../../helpers/assignment/booked-total';
import {
  formatAssignmentProbability,
  getAssignmentProbabilityClassName,
  getAssignmentProbabilityIndicatorClassName,
  isAssignmentProbabilityVisible,
} from '../../helpers/assignment/probability';
import { getProjectStatusBadgeProps } from '../../helpers/project-status-badge';
import { getRateSourceLabel } from '../../helpers/rate-option';
import { AssignmentProject } from '../../types/assignment-project';
import { AssignmentRate } from '../../types/assignment-rate';
import { AssignmentStatus } from '../../types/assignment-status';
import { ScrollableText } from '../assignment/text/scrollable-text';

interface AssignmentTooltipContentProps {
  user?: OpsiumUserShortResponse;
  project?: AssignmentProject;
  color: string;
  status: AssignmentStatus;
  probability?: number | null;
  isBillable?: boolean;
  originalStartDate: string;
  originalEndDate: string;
  hoursPerDay: number;
  /** Whole-period total, net of time off; null hides it. Unlike `hoursPerDay`, not fragment-scoped. */
  totalBookedMinutes?: number | null;
  note?: string;
  isBackgroundWork?: boolean;
  fragmentRate?: AssignmentRate | null;
  fragmentRateChangeDate?: dayjs.Dayjs | null;
  hasRateChanges: boolean;
  isCutting: boolean;
  isDuplicating: boolean;
  disableCutting: boolean;
  disabled: boolean;
  isDeleteAssignmentEnabled: boolean;
  isCreateAssignmentEnabled: boolean;
  isEditAssignmentEnabled: boolean;
  onEditClick: () => void;
  onToggleCutting: () => void;
  onDeleteClick: () => void;
  onToggleDuplicating: () => void;
}

function IconCell({ icon: Icon, className }: { icon: LucideIcon; className?: string }) {
  return (
    <div className="flex items-center justify-center p-1">
      <Icon size={14} className={`flex-shrink-0 ${className ?? ''}`} />
    </div>
  );
}

export const AssignmentTooltipContent = memo(
  ({
    user,
    project,
    color,
    status,
    probability,
    isBillable,
    originalStartDate,
    originalEndDate,
    hoursPerDay,
    totalBookedMinutes,
    note,
    isBackgroundWork,
    fragmentRate,
    fragmentRateChangeDate,
    hasRateChanges,
    isCutting,
    isDuplicating,
    disableCutting,
    disabled,
    isDeleteAssignmentEnabled,
    isCreateAssignmentEnabled,
    isEditAssignmentEnabled,
    onEditClick,
    onToggleCutting,
    onDeleteClick,
    onToggleDuplicating,
  }: AssignmentTooltipContentProps) => {
    const { t } = useTranslation(['authenticated.planner', 'common']);

    const hoursPerDayString = useMemo(
      () => t('common:time.perDay', { time: formatHoursToTimeString(hoursPerDay) }),
      [hoursPerDay, t]
    );

    const totalBookedString = useMemo(
      () =>
        totalBookedMinutes === null || totalBookedMinutes === undefined
          ? null
          : t('totalBookedHours', {
              time: formatTotalBookedHours(totalBookedMinutes),
            }),
      [totalBookedMinutes, t]
    );

    // Build structured rate info for tooltip (hidden when non-billable since rate is always 0).
    // No permission check: a rate the user may not see arrives already masked from the backend.
    // The source label is always shown — it says which kind of billing applies, not what it costs —
    // but the masked value itself is dropped rather than rendered as a glyph. A masked figure tells
    // the reader nothing here; the place it is worth showing is the assignment modal, where it can
    // be replaced.
    const rateInfo = useMemo(() => {
      if (!isBillable || !fragmentRate) return null;

      const { assignmentRateSource, projectBoundRateSource, rate, currency } = fragmentRate;
      const rateValue = RateMask.isMasked(rate) ? null : `${rate} ${currency.code} / hr`;
      const isProjectBased =
        assignmentRateSource === RateSource.ProjectBased && !!projectBoundRateSource;
      const sourceLabel = isProjectBased
        ? t('common:rates.projectBasedShort')
        : getRateSourceLabel(assignmentRateSource);
      const innerSourceLabel = isProjectBased ? getRateSourceLabel(projectBoundRateSource) : null;
      // Only show "starting from" date when there are multiple distinct rates across periods
      const dateLabel =
        hasRateChanges && fragmentRateChangeDate
          ? t('tooltip.rateStartingFrom', {
              date: formatDisplayDate(fragmentRateChangeDate.toDate()),
            })
          : null;

      return { sourceLabel, innerSourceLabel, rateValue, dateLabel };
    }, [isBillable, fragmentRate, hasRateChanges, fragmentRateChangeDate, t]);

    const dateRangeString = useMemo(
      () => formatCompactDateRange(originalStartDate, originalEndDate),
      [originalStartDate, originalEndDate]
    );
    const shouldShowProbability = isAssignmentProbabilityVisible(status);
    const isProbabilityMissing = probability === null || probability === undefined;

    // Flags the project state under the project name (see
    // getProjectStatusBadgeProps for the badge/tooltip rules). The label says
    // "project" so the badge can't be confused with the assignment status
    // badge below.
    const projectStatusBadge = useMemo(() => getProjectStatusBadgeProps(project, t), [project, t]);

    return (
      <Command className="max-w-72">
        {/* Action buttons: edit, cut, delete, duplicate */}
        <div className="flex justify-end gap-1 px-3 py-1">
          {isEditAssignmentEnabled && (
            <>
              <Button
                variant="ghost"
                size="icon"
                onClick={onEditClick}
                aria-label={t('common:edit')}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant={isCutting ? 'secondary' : 'ghost'}
                size="icon"
                onClick={onToggleCutting}
                data-cut-toggle
                disabled={disableCutting || disabled}
              >
                <ScissorsIcon className="h-4 w-4" />
              </Button>
            </>
          )}
          {isDeleteAssignmentEnabled && (
            <Button
              variant="ghost"
              size="icon"
              onClick={onDeleteClick}
              aria-label={t('common:delete')}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
          {isCreateAssignmentEnabled && (
            <Button
              variant={isDuplicating ? 'secondary' : 'ghost'}
              size="icon"
              onClick={onToggleDuplicating}
              disabled={disabled}
            >
              <Copy className="h-4 w-4" />
            </Button>
          )}
        </div>

        <CommandSeparator />

        <div className="flex flex-col gap-3 p-3">
          {/* Identity block: the project is the hero (in a subtle card), the assigned
              member is the secondary "who" row directly below it. A divider closes the
              block off from the assignment details. */}
          <div className="flex flex-col gap-2">
            <Card className="flex flex-col gap-1.5 bg-muted/40 p-2.5 shadow-none">
              {/* Project: color dot + neutral name (the dot already conveys the color,
                  so the text is NOT tinted). */}
              <div className="flex min-w-0 items-center gap-2">
                <div className="h-2 w-2 flex-shrink-0 rounded-full" style={{ background: color }} />
                <div className="min-w-0 flex-1 overflow-hidden">
                  <ScrollableText
                    className="text-sm font-semibold leading-5"
                    animationDelayMs={100}
                  >
                    {project?.name}
                  </ScrollableText>
                </div>
              </div>
              {project?.clientName && (
                <div className="min-w-0 overflow-hidden pl-4">
                  <ScrollableText
                    className="text-xs leading-none text-muted-foreground"
                    animationDelayMs={100}
                  >
                    {project.clientName}
                  </ScrollableText>
                </div>
              )}
              {/* Project status — its own row, under the client. */}
              {projectStatusBadge && (
                <div className="pl-4">
                  <StatusBadge
                    type={StatusBadgeType.Project}
                    status={projectStatusBadge.status}
                    label={projectStatusBadge.label}
                    size={StatusBadgeSize.Sm}
                    warningTooltip={projectStatusBadge.warningTooltip}
                    infoTooltip={projectStatusBadge.infoTooltip}
                  />
                </div>
              )}
            </Card>

            {/* Assigned member — secondary identity: "assigned to" icon + avatar + name. */}
            {user?.name && (
              <div className="flex min-w-0 items-center gap-2 pl-0.5">
                <UserRoundArrowLeftIcon size={16} className="flex-shrink-0 text-muted-foreground" />
                <EntityAvatar
                  image={user.profileImageUrl}
                  name={user.name}
                  size="xs"
                  shape="rounded"
                />
                <div className="min-w-0 overflow-hidden">
                  <ScrollableText className="text-sm leading-4" animationDelayMs={100}>
                    {user.name}
                  </ScrollableText>
                </div>
              </div>
            )}
          </div>

          {/* Visually separates the main identity block from the assignment details. */}
          <Separator className="w-auto" />

          {/* Assignment details rows */}
          <div className="flex flex-col gap-1">
            {/* Status */}
            <div className="flex items-center gap-1">
              <IconCell icon={CheckCircle} />
              <StatusBadge type={StatusBadgeType.Assignment} status={status} label={status} />
            </div>

            {shouldShowProbability && !isProbabilityMissing && (
              <div className="flex items-center gap-1.5">
                <IconCell icon={Percent} />
                <p className="text-sm leading-4">
                  {t('tooltip.probability')}{' '}
                  <span
                    className={cn(
                      'mr-1 inline-block h-2 w-2 rounded-full align-middle',
                      getAssignmentProbabilityIndicatorClassName(probability)
                    )}
                  />
                  <span className={getAssignmentProbabilityClassName(probability)}>
                    {formatAssignmentProbability(probability)}
                  </span>
                </p>
              </div>
            )}

            {/* Billable / non-billable */}
            <div className="flex items-center gap-1.5">
              <div className="flex items-center justify-center p-1">
                {isBillable ? (
                  <CircleDollarSign size={14} className="flex-shrink-0" />
                ) : (
                  <DollarSignOffIcon size={14} className="flex-shrink-0 text-destructive" />
                )}
              </div>
              <p className="text-sm leading-4">
                {isBillable ? t('billable') : t('userInfo.nonBillable')}
              </p>
            </div>

            {/* Rate: source label + optional nested breakdown with rate value and effective date */}
            {rateInfo && (
              <div className="flex items-start gap-1.5">
                <IconCell icon={Banknote} />
                <div className="flex flex-col text-sm leading-5">
                  <span>{rateInfo.sourceLabel}</span>
                  {rateInfo.innerSourceLabel || rateInfo.dateLabel ? (
                    <div className="ml-1 flex flex-col border-l border-muted-foreground/40 pl-2">
                      {rateInfo.innerSourceLabel && <span>{rateInfo.innerSourceLabel}</span>}
                      {rateInfo.rateValue && <span>{rateInfo.rateValue}</span>}
                      {rateInfo.dateLabel && (
                        <span className="text-muted-foreground">{rateInfo.dateLabel}</span>
                      )}
                    </div>
                  ) : (
                    rateInfo.rateValue && <span>{rateInfo.rateValue}</span>
                  )}
                </div>
              </div>
            )}

            {/* Date range */}
            <div className="flex items-center gap-1.5">
              <IconCell icon={CalendarIcon} />
              <p className="text-sm leading-4">{dateRangeString}</p>
            </div>

            <div className="flex items-center gap-1.5">
              <IconCell icon={ClockIcon} />
              <p
                className="flex flex-wrap gap-x-1 text-sm leading-4"
                data-testid="planner-tooltip-hours"
              >
                <span>{hoursPerDayString}</span>
                {totalBookedString && (
                  <span className="text-muted-foreground">{totalBookedString}</span>
                )}
              </p>
            </div>

            {/* Note (optional) */}
            {note && (
              <div className="flex max-w-44 items-center gap-1.5 overflow-hidden">
                <IconCell icon={StickyNote} />
                <TruncatedTooltip content={note} preserveWhitespace className="text-sm" />
              </div>
            )}

            {/* Background work indicator (optional) */}
            {isBackgroundWork && (
              <div className="flex items-center gap-1.5">
                <IconCell icon={Layers} className="text-destructive" />
                <p className="text-sm leading-4">{t('form.isBackgroundWork.labelPlanner')}</p>
                <SimpleTooltip content={t('form.isBackgroundWork.tooltipPlanner')}>
                  <Info size={12} className="flex-shrink-0 cursor-help text-muted-foreground" />
                </SimpleTooltip>
              </div>
            )}

            {shouldShowProbability && isProbabilityMissing && (
              <div className="flex items-center gap-1.5 text-warning">
                <IconCell icon={Info} />
                <p className="text-sm leading-4">{t('tooltip.probabilityMissing')}</p>
              </div>
            )}
          </div>
        </div>
      </Command>
    );
  }
);

AssignmentTooltipContent.displayName = 'AssignmentTooltipContent';
